import {
  withLatestFrom,
  filter,
  map,
  distinctUntilChanged,
  debounceTime,
  first,
} from 'rxjs/operators';
import { of, Subscription, combineLatest } from 'rxjs';
import { Store } from '@ngrx/store';
import { AutoUnsubscribe } from 'ngx-auto-unsubscribe';
import {
  Component,
  ElementRef,
  Renderer2,
  OnDestroy,
  OnInit,
  AfterViewInit,
  ViewChildren,
  QueryList,
} from '@angular/core';

import { Roles } from '../../../models/roles/roles.enum';
import { AppState } from '../../../interfaces/app-state.interface';
import { OrderType } from '../../../enum/orders/order-type.enum';
import { KeyStocks } from '../../../enum/key-stocks/key-stocks.enum';
import { OrderStatus } from '../../../enum/orders/order-status.enum';
import { OrderSideEnum } from '../../../enum/orders/side.enum';
import { BillingAction } from '../../../state-management/billing/billing.action';
import { ResearchActions } from '../../../state-management/research/research.action';
import { UtilitiesService } from '../../../services/utilities.service';
import { SuitabilityAction } from '../../../state-management/suitability/suitability.action';
import { StocksMarketAction } from '../../../state-management/stocks-market/stocks-market.action';
import { SuitabilityEffects } from '../../../state-management/suitability/suitability.effects';
import { ResearchAdviceLite } from '../../../models/research/research-opportunity.model';
import { OrderMarketTypeEnum } from '../../../enum/orders/market-type.enum';
import { NotificationFactory } from '../notifications-queue--item/notification.factory';
import { NotificationsQueueItem } from '../notifications-queue--item/notifications-queue--item.component';
import { NotificationsQueueService } from './notifications-queue.service';
import { StocksExchangeWalletAction } from '../../../state-management/stocks-exchange-wallet/stocks-exchange-wallet.action';
import { StocksExchangeWalletEffects } from '../../../state-management/stocks-exchange-wallet/stocks-exchange-wallet.effects';
import { InfoproductsPurchaseService } from '../../../services/infoproducts/infoproducts-purchase.service';

@AutoUnsubscribe()
@Component({
  selector: 'notifications-queue',
  templateUrl: 'notifications-queue.component.html',
  styleUrls: ['notifications-queue.component.scss'],
})
export class NotificationsQueue implements OnDestroy, OnInit, AfterViewInit {
  @ViewChildren(NotificationsQueueItem)
  public notificationsQueueItems: QueryList<NotificationsQueueItem>;

  public notificationsItens: any[] = [];
  public ordersArray: any[] = [];
  private queue: any;
  private queueContainer: any;
  private queueSubscription: Subscription;
  private walletEffectsSubscription: Subscription;
  private researchSubscription: Subscription;
  private validStrategiesSubscription: Subscription;
  private walletAndProfileSubscriptions: Subscription;
  private hasCBLC: boolean = false;
  private symbol: string = '';
  private advice: ResearchAdviceLite = null;
  private suitability: any;

  constructor(
    public elementRef: ElementRef,
    public walletEffects: StocksExchangeWalletEffects,
    public suitabilityEffects: SuitabilityEffects,
    private renderer: Renderer2,
    private store: Store<AppState>,
    private utilitiesService: UtilitiesService,
    private notificationQueueService: NotificationsQueueService,
    private infoproductsPurchaseService: InfoproductsPurchaseService
  ) {
    this.walletEffectsSubscription = this.store
      .select((s) => s.authentication.currentUser)
      .pipe(
        filter((user) => user && user.role !== Roles.Identified),
        map((user) => user.role),
        distinctUntilChanged()
      )
      .subscribe((user) => {
        this.hasCBLC = true;
        this.store.dispatch({
          type: StocksExchangeWalletAction.getInvestments,
        });
      });

    this.validStrategiesSubscription = this.store
      .select((s) => s.stocksExchange.wallet.validStrategies)
      .pipe(
        filter((strategies) => (strategies ? true : false)),
        map((strategies) =>
          Object.keys(strategies).map((strategy) => strategies[strategy])
        ),
        filter((strategies: any[]) =>
          strategies.every((strategy) => (strategy.legs ? true : false))
        ),
        map((strategies: any[]) => {
          return strategies
            .map((strategy) =>
              strategy.legs.find(
                (leg) => leg.internalOrderType === OrderType.Input
              )
            )
            .filter((strategy) => strategy);
        })
      )
      .subscribe((strategies) => {
        this.ordersArray = strategies.filter(
          (order) =>
            order.status === OrderStatus.Filled ||
            order.status === OrderStatus.PartiallyFilled
        );
      });

    const observablesWithCBLC = (): any[] => {
      if (this.hasCBLC) {
        return [
          this.walletEffects.getInvestments$.pipe(
            filter((action: any) =>
              StocksExchangeWalletAction.getInvestmentsSuccess.equals(
                action.type
              )
            )
          ),
          this.suitabilityEffects.getProfile$.pipe(
            filter((action: any) =>
              action.type === SuitabilityAction.getProfileSuccess
            )
          ),
        ];
      } else {
        return [of([])];
      }
    };

    this.walletAndProfileSubscriptions = combineLatest(observablesWithCBLC())
      .pipe(first())
      .subscribe(() =>
        this.store.dispatch({
          type: ResearchActions.reasearchAdvicesSubscribe,
        })
      );

    this.store.dispatch({ type: SuitabilityAction.getProfile });
    this.store.dispatch({ type: BillingAction.getBrokerageModel });
  }

  /**
   * Verifica permissão para notificações de infoprodutos
   * @param notification notificação
   * @param subscriptionData infoprodutos que o usuário tem direito
   */
  public checkInfoProductPermission(
    { category }: ResearchAdviceLite,
    allowedRecommendations: Map<string, string>
  ) {
    return (
      !!category &&
      (!this.infoproductsPurchaseService.isInfoProduct(category.id) ||
        allowedRecommendations.has(
          this.utilitiesService.createSlug(category.name)
        ))
    );
  }

  /**
   * Verifica se o usuário pode receber notificações da categoria Toro+
   * @param categoryId
   * @param infoProductPermission
   */
  public canReceiveToroPlusNotification(
    categoryId: number,
    infoProductPermission: boolean
  ): boolean {
    return (
      this.infoproductsPurchaseService.isToroPlusCategory(categoryId) &&
      infoProductPermission
    );
  }

  /**
   * Verifica se o usuário pode receber notificações da categoria Turbo Trader
   * @param categoryId
   * @param infoProductPermission
   */
  public canReceiveTurboTraderNotification(
    categoryId: number,
    infoProductPermission: boolean
  ): boolean {
    return (
      this.infoproductsPurchaseService.isTurboTraderCategory(categoryId) &&
      infoProductPermission
    );
  }

  public ngOnInit() {
    this.store
      .select((state) => state.suitability)
      .pipe(filter((result) => !!(result && result.profile)))
      .subscribe((result) => {
        this.suitability = result.profile.description;
      });

    this.store
      .select(({ research }) => research && research.advice)
      .pipe(
        withLatestFrom(
          this.store.select(
            ({ infoproducts }) =>
              infoproducts && infoproducts.allowedRecommendations
          )
        )
      )
      .pipe(
        debounceTime(1000),
        filter(
          ([advice, allowedRecommendations]) =>
            !!advice && !!allowedRecommendations.size
        ),
        filter(([advice, allowedRecommendations]) =>
          this.checkInfoProductPermission(advice, allowedRecommendations)
        )
      )
      .subscribe(([advice]) => {
        // Notificação disponível apenas para usuários com perfil ARROJADO
        if (advice.avaliable && this.suitability === 'Arrojado') {
          this.advice = advice;
          this.symbol = advice.symbolID;

          this.store.dispatch({
            type: StocksMarketAction.getQuote,
            payload: this.symbol,
          });
          if (
            this.advice.instrument.typeMarket === OrderMarketTypeEnum.Future
          ) {
            this.store.dispatch({
              type: StocksMarketAction.getQuote,
              payload: KeyStocks.DolPtax,
            });
          }

          this.notificationsItens.push(
            new NotificationFactory({
              type:
                this.advice.side === OrderSideEnum.Compra
                  ? 'RecommendationBuy'
                  : 'RecommendationSale',
              symbol: this.symbol,
              advice: this.advice,
            })
          );

          this.activeQueue();
        } else {
          this.removeNotification(advice);
        }
      });
  }
  /**
   * Método que faz a lógica de encerramento de notificação,
   * verificando pelo ID da notificação desabilitada, e adicionando animação.
   * @param advice objeto de notificação
   */
  public removeNotification(advice: any) {
    const elementId = advice.id;
    this.notificationsQueueItems.toArray().forEach((item, index) => {
      if (item.advice.id === elementId) {
        // Remove card
        item.renderer.addClass(
          item.elementRef.nativeElement.children[0],
          'unavailable'
        );
        setTimeout(() => {
          this.removeItem(index);
        }, 360);
        // Aplica a animação para o elemento que deve tomar o lugar do card que foi removido.
        this.animateNextCard(index);
      }
    });
  }

  /**
   * Método que remove item da lista de notificações.
   */
  public removeItem(index: number) {
    if (this.notificationsItens.length > 0) {
      this.notificationsItens.splice(index, 1);
    } else {
      this.notificationsItens = [];
    }
    this.activeQueue();
  }

  /**
   * Método que faz animação do próximo item da lista ser arrastado para ocupar o lugar
   * do card de notificação que foi removido.
   * @param index number
   */
  public animateNextCard(index: number) {
    this.notificationsQueueItems.toArray().forEach((item, i) => {
      if (index === i) {
        item.renderer.addClass(item.elementRef.nativeElement, 'on-entering');
        setTimeout(() => {
          item.renderer.removeClass(
            item.elementRef.nativeElement,
            'on-entering'
          );
        }, 360);
      }
    });
  }

  public ngAfterViewInit() {
    this.queue = this.elementRef.nativeElement.querySelector(
      '#notifications-queue'
    );
    this.queueContainer = this.elementRef.nativeElement.querySelector(
      '#notifications-queue--container'
    );
    this.subscribe_QueueItens();
  }

  /**
   * EMITTERS LISTENERS
   */
  public identifyLast() {
    this.increaseWidthQueueContainer();
  }

  /**
   * Incrementa a largura do container de acordo com os itens adicionados
   */
  public increaseWidthQueueContainer() {
    if (this.queueContainer) {
      const queueItens: any[] = Array.from(this.queueContainer.children);
      let pxLength = 0;

      queueItens.forEach((item) => {
        pxLength += Number(item.clientWidth);
      });

      if (queueItens.length > 2)
        this.queueContainer.style.width = pxLength + 'px';
    }
  }

  /**
   * A fila é ativada quando há itens nela, e dasativada quando não há itens
   */
  public activeQueue() {
    if (!this.queue) return;
    if (this.notificationsItens.length) {
      this.renderer.addClass(this.queue, 'active');
    } else {
      this.renderer.removeClass(this.queue, 'active');
    }
  }

  /**
   * GET AND/OR UPDATE
   * methods to get/update amount of notifications and menu notifications
   */
  public subscribe_QueueItens() {
    this.queueSubscription =
      this.notificationQueueService.queueItensObservable$.subscribe(
        (queueItensArray) => {
          this.notificationsItens = queueItensArray;
          this.activeQueue();
        }
      );
    this.notificationQueueService.getAndUpdate_QueueItens();
  }

  /**
   * Filtra as recomendações
   * @param item recomendação
   * @param investments Lista de investimentos do usuário
   */
  public filterAdvices(item: any, investments: any[]): boolean {
    return investments.some(
      (investment) =>
        item.SymbolID === investment.symbol &&
        item.IsDayTrade === investment.isDayTrade
    );
  }

  /**
   * não remover, necessário para o funcionamento correto do @AutoUnsubscribe
   */
  public ngOnDestroy() {
    this.store.dispatch({ type: ResearchActions.reasearchCleanAdvice });
  }
}
